import * as _  from "lodash";
import { Observable, from, Subscription, pipe, of } from 'rxjs';
import { finalize, catchError } from 'rxjs/operators';
import { Component, OnInit, ViewChild } from '@angular/core';
import { EventEmitter } from "@angular/core";

import {MessageService} from 'primeng/api';

import { ConfigurationService } from './../connect-configuration/configuration.service';
import { RelationshipTypeConfiguration, ConfigurationType } from './../connect-configuration/configuration';
import { RelationshipType as PartnershipType, RelationshipType, Configuration } from './../../partnership/models/partner';
import { TabView, TabPanel } from 'primeng/components/tabview/tabview';
import { injectArgs } from '@angular/core/src/di/injector_compatibility';

import {ViewEncapsulation} from '@angular/core';

@Component({
  selector: 'app-connect-configuration',
  templateUrl: './connect-configuration.component.html',
  styleUrls: ['./connect-configuration.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers:[MessageService]
})

export class ConnectConfigurationComponent implements OnInit {
  
  modal   : boolean = false;
  loading : boolean = false;
  syncing : boolean = false;
  formValid = false;
  group = false;

  displayTitle      : string;
  displayViewDialog : boolean;
  
  currentTabData: any[] = [];
  currentTabIndex: number = 0;

  /* Configuration types (categories) */
  relationshipTypes: RelationshipType[];
  
  data: any = {};
  selectedData = [];

  sectionsDisabled = {};
  
  /* maintains state for the current set of configurations */
  stateModel = {dirty:false};

  subscription: Subscription;
  emitter: EventEmitter<any> = new EventEmitter();

  /* 
    Configuration types - settings 
    Currently displayed as checkbox enabled booleans 
    and conditionally multiselect dropdowns
  */
  relationshipTypeConfigurations: RelationshipTypeConfiguration[] = [];

  selectedConfigurationType : ConfigurationType;
  selectedRelationshipType  : RelationshipTypeConfiguration;

  @ViewChild('tabs') tabs: TabView;

  constructor( 
    private _configService: ConfigurationService ,
    private _msgService: MessageService
  ) {
    this.emitter.subscribe(event=>{
      switch (event.eventKey) {
        case 'LOAD-RELATIONSHIP-TYPES':
          this.syncing = true;
          this._loadRelationshipTypes();
          break;
        case 'TAB-CHANGED':
        case 'LOAD-TYPES-RESET':
        case 'LOAD-TYPES-COMPLETE':
        case 'LOAD-TYPES-CONFIGURATIONS':
          this.syncing = true;
          this._msgService.add({sticky:true, severity:'success', summary:'Loading', detail:'Loading Configurations. Please wait.'})
          this._loadRelationshipTypeConfigurations(event.index);
        break;
        case 'SET-CONTROL-STATUS':
          this._msgService.clear();
          this._setControlState(event.index);
        break;
        case 'SAVE-SUCCESS':
          this.syncing = false;
          this._msgService.clear();
          this._msgService.add({severity:'success', summary:'Saving Configuration', detail:'Configuration successfully saved.'})
        break;
        case 'SAVE-FAIL':
          this.syncing = false;
          this._msgService.clear();
          this._msgService.add({severity:'error', summary:'An error has occurred', detail:'Failed to save configuration.'})
        break;
        case 'SYNCING-DATA':
          this.syncing = true;
          this._msgService.add({sticky:true, severity:'warn', summary:'Saving Configuration', detail:'Saving your configuration.'})
        break;

        default:
        break;
      }
    });
  }

  ngOnInit() {
    this.emitter.emit({eventKey:'LOAD-RELATIONSHIP-TYPES'});
    this.tabs.open = this.onTabChange.bind(this);
  }

  /* STEP ONE - populate relationship types */
  private _loadRelationshipTypes(){
  
    this._configService
      .findRelationshipTypes()
      .pipe(finalize(()=>this.syncing = false))
      .subscribe(rtypes => {
        this.syncing = true;
        (<any>this).relationshipTypes = (<any>rtypes).data;

        /* notify types fetched */
        this.emitter.emit({eventKey:'LOAD-TYPES-COMPLETE', index:0});
    });

  }

  /* 
    STEP TWO
    Associate downloaded JSON model values to DOM elements by reference
    Yes this sucks badly and will be brittle. There was no way to really use a 
    form with this based on the data structure and the UI design.
  */
  private _loadRelationshipTypeConfigurations(idx){
    let relationshipType = this.relationshipTypes[idx];
    
    this._configService
      .findByRelationshipTypeID(relationshipType.RelationshipTypeId)
      .subscribe(data => {
        this.syncing = true;

        /* reset statemodel */
        this.stateModel = {dirty:false};

          /* 
            Relationship types are stored/referenced by tab index
            Here we nest the configurations and the settings
          */
          (<any>this.relationshipTypes[idx]).Configuration = (<any>data).data[0].RelationshipConfigurations;
            this.data = {RelationshipType:(<any>data).data[0].RelationshipType};
            /* 
              Here we iterate the "Configuration" which are parent child relationships
              There are a few rules to keep in mind.
                1. Selected   - checkbox
                2. HasDetail  - this means that there is an dictionary of settings associated with this element.
                3. Selections - these are the choices "currently represented in the UI as options".
            */
            from((<any>this.relationshipTypes[idx]).Configuration)
              .pipe(finalize(()=>this.syncing = false))
              .subscribe(config=>{
                let parent = <any>config;

                this.stateModel[parent.AccessTypeKey] = parent;

                this.sectionsDisabled[parent.AccessTypeKey]=!parent.Selected;

                /* mark selections for milti dropdown */
                if(parent.HasDetail){
                  this.stateModel[parent.AccessTypeKey].Choices = parent.Selections.filter(item=>item.Selected);
                }
                
                /* add children */
                if(parent.Children.length){
                  this.stateModel[parent.AccessTypeKey].Children = parent.Children;

                  from(parent.Children)
                    .subscribe((child:any) => {
                      if(child.HasDetail){
                        child.Choices = child.Selections.filter(item=>item.Selected);
                      }
                    });

                }
                this.formValidate();
              });

          this.emitter.emit({eventKey:'SET-CONTROL-STATUS', index:idx});
          this._msgService.clear();
      },
        err => this._msgService.add({severity:'error', summary:'An Error Has Occured', detail:'Error Loading Configurations.'})
      );
  }

  /* STEP THREE - _setControlStatus */
  private _setControlState(index){
    let configs = from((<any>this).relationshipTypes[index].Configuration);

      configs.subscribe(
        config => {
          /* disable all sections by default */
          this.sectionsDisabled[(<any>config).AccessTypeKey] = !(<any>config).Selected;
        }
      );

    this.emitter.emit({eventKey:'LOAD-TYPE-SETTINGS-COMPLETE'});
  }

  isDirty(){
    return this.stateModel.dirty;
  }

  setDirty(){
    this.stateModel.dirty = true;
    this.formValidate();
  }

  resetChildSelection(configData: Configuration, selectedValue: boolean): void {
    configData.Children.map(config => {
      this.relationshipTypes[this.currentTabIndex].Configuration.map((configItem, oIndex) => {
        configItem.Children.map(childItem => {
          if (childItem.AccessTypeId === config.AccessTypeId) {
            if (!selectedValue) {
              childItem.Selected = selectedValue;
            }
          }
        });
      });
    });
  }

  toggleEnabled(accessTypeKey){
    this.sectionsDisabled[accessTypeKey]=(!this.sectionsDisabled[accessTypeKey]);
  }

  /* ====================== HANDLERS ====================== */

  onTabChange(event: Event, tab: TabPanel){
    var tabIndex = this.currentTabIndex;
    /* confirm lose changes */
    if(this.stateModel.dirty){
      this._msgService.add({
        data:{
          event:event,
          tab:tab,
          tabChangeArgs:arguments,
          currentTabIndex: tabIndex,
          eventKey:'TAB-CHANGED-DIRTY'
        },
        key: 'confirmAction',
        sticky: true, 
        severity:'warn',
        summary:'You have unsaved changes on this Page!',
        detail:'Clicking Yes will leave the page and discard any changes you have made.'
      });
      return false;
    };

    /* change tab */
    TabView.prototype.open.apply(this.tabs, arguments);
   return this.emitter.emit({eventKey:'TAB-CHANGED', index:this.currentTabIndex});
  };
  
  /* 
    Set the state of the Selections[n].selected value
    When checking the "select all" checkbox three things to note
      1. event.itemValue DOES NOT EXIST
      2. true  : ALL values are sent in: event.value
      3. false : NO  values are sent in: event.value
  */
  onChangeSetting(event, parent, childKey){
    var state = (event.value.length)? true: false;
    let setState = function(collection, state){
      from(collection).subscribe(elem => (<any>elem).Selected = state);
    };

    /* SINGLE SELECTION - just !boolean*/
    if(event.hasOwnProperty('itemValue')){
      event.itemValue.Selected = !event.itemValue.Selected;
      return;
    }
    /* ALL SELECTED - event.value contains all settings */
    if(event.value.length){
      from(event.value).subscribe((setting:any)=>setting.Selected=true)
      return;
    }
    
    /* NONE SELECTED - do lookup through parent since we get no settings*/
    if (!event.value.length) {
        setState(parent, false);
        return;
      }
  }

  clickDetail(event, collection){
    if(!collection.Choices.length) return;
    this.group = (collection.AccessTypeKey === 'LimitPropertyLibraryByItems') ? true : false;
    
    this.modal = true;
    this.selectedData = collection.Choices;
    
  }

  clickCancel(){
    /* if no changes made then no need to reload */
    if(!this.stateModel.dirty) return;

    this._msgService.add({
      data:{
        eventKey:'CANCEL-CHANGES',
        index: this.currentTabIndex
      },
      key: 'confirmAction',
      sticky: true, 
      severity:'warn',
      summary:'Are you sure you want to discard all changes?', 
      detail:'Clicking "Yes" will undo your current changes and reset the config back to when the page was loaded.'
    });
  }

  /* 
    CONFIRM PROCEED to change tab || cancel
  */
  onConfirmYes(data) {
    switch (data.eventKey) {
      case 'TAB-CHANGED-DIRTY':
        this._msgService.clear('confirmAction');
        TabView.prototype.open.apply(this.tabs, data.tabChangeArgs);
        return this.emitter.emit({eventKey:'TAB-CHANGED', index:this.currentTabIndex});
      break;
      case 'CANCEL-CHANGES':
        this.emitter.emit({eventKey:'LOAD-TYPES-RESET', index:this.tabs.activeIndex});
      break;
    
      default:
        break;
    }
  }

  /* 
    Stops the current action
    ABORT TAB CHANGE || CANCEL
  */
  onConfirmNo(data) {
    if(data.eventKey === 'TAB-CHANGED-DIRTY'){        
      this.currentTabIndex = data.currentTabIndex;
    }
    this._msgService.clear('confirmAction');
  }

  clickSave(){
    if(this.syncing) return;
    let replacer = function(key, value){
      if(key == 'Selections'){ return undefined };
      return value;
    };
    let rtype = this.relationshipTypes[this.currentTabIndex];
  
    this.emitter.emit({eventKey:'SYNCING-DATA'})

    /* remove Selections */
    let rtypeString = JSON.stringify(rtype, replacer);

    /* rename Selected to Selections */
    rtypeString = rtypeString.replace(/\"Choices\":/g, "\"Selections\":");

    /* revive JSON */
    let newRtype = JSON.parse(rtypeString);

    this.data.RelationshipConfigurations=newRtype.Configuration;

    this._configService
      .save(this.data)
      .subscribe(
        res => {
          this.emitter.emit({eventKey:'SAVE-SUCCESS', response:res})
          this.syncing = false;
          this.stateModel.dirty = false;
        },
        err => this.emitter.emit({eventKey:'SAVE-FAIL', error:err})
      );

  }

  formValidate() {
    this.formValid = true;

    if (this.relationshipTypes && this.relationshipTypes.length
        && (this.relationshipTypes[this.currentTabIndex]['Configuration'])) {
      const config = (<any>this.relationshipTypes[this.currentTabIndex]).Configuration;
      for (let i = 0; i < config.length; i++) {
        if (config[i].HasDetail && config[i].Selected && config[i].Choices && !config[i].Choices.length) {
            this.formValid = false;
            return;
        }
        for (let j = 0, child = config[i].Children; j < config[i].Children.length; j++) {
          if (config[i].Children[j].HasDetail) {
             if (child[j].Selected && child[j].Choices && !child[j].Choices.length) {
              this.formValid = false;
            }
          }
        }
      }
    }
  }

}
